import { ActionTree } from 'vuex';
import ILocalState from './stateInterface';
import { AjaxService } from '@/services/AjaxService';
import { ajaxUrls } from '@/helpers/ajaxUrlsHelper';
import { TJsonApiBody } from 'jsona/lib/JsonaTypes';
import { ProductGroupTypes } from '@/enums/ProductGroupTypes';

const actions: ActionTree<ILocalState, {}> = {
    async getMyProjects(context, productGroup) {
        // @ts-ignore
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (token) {
            const targetUrl =
                productGroup !== ProductGroupTypes.None
                    ? ajaxUrls.projects.projectsListWithGroup(productGroup)
                    : ajaxUrls.projects.projectsListSorted;
            const myProjectsList = (await AjaxService.get(
                targetUrl,
                token,
            )) as TJsonApiBody;

            if (productGroup !== ProductGroupTypes.None) {
                moveMainFolderToBottom(myProjectsList);
            }
            context.commit('setMyProjectsList', myProjectsList);
        } else {
            context.dispatch('user/logoutUser', { root: true });
            throw new Error("Token doesn't exist");
        }
    },
    async getExistingProjectsWithGroup(context, productGroup) {
        // @ts-ignore
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (token) {
            const myProjectsList = (await AjaxService.get(
                ajaxUrls.projects.existingProjectsWithGroup(productGroup),
                token,
            )) as TJsonApiBody;

            context.commit('setMyProjectsList', myProjectsList);
        } else {
            context.dispatch('user/logoutUser', {
                root: true,
            });
            throw new Error("Token doesn't exist");
        }
    },
    deleteSingleProject(context, id) {
        return new Promise(async (resolve, reject) => {
            // @ts-ignore
            const token = window.$cookies.isKey('token') ? window.$cookies.get('token') : null;
            if (token) {
                await AjaxService.delete(
                    ajaxUrls.projects.singleProject(id),
                    token,
                );
                context.dispatch(
                    'getMyProjects',
                    context.rootGetters['user/activeProductGroup'],
                );
                resolve();
            } else {
                reject(new Error('Token exipred'));
            }
        });
    },
    updateProject(context, projectData) {
        return new Promise(async (resolve, reject) => {
            // @ts-ignore
            const token = window.$cookies.isKey('token') ? window.$cookies.get('token') : null;
            if (token) {
                try {
                    await AjaxService.patch(
                        ajaxUrls.projects.singleProject(projectData.id),
                        {
                            data: {
                                type: 'projects',
                                id: projectData.id,
                                attributes: {
                                    prodGroup: projectData.prodGroup,
                                    description: projectData.description,
                                    name: projectData.name,
                                },
                            },
                        },
                        token,
                    );
                } catch (e) {
                    reject(e);
                }
                context.dispatch(
                    'getMyProjects',
                    context.rootGetters['user/activeProductGroup'],
                );
                resolve();
            } else {
                reject(new Error('Token exipred'));
            }
        });
    },
    addProject(context, projectData) {
        return new Promise(async (resolve, reject) => {
            // @ts-ignore
            const token = window.$cookies.isKey('token') ? window.$cookies.get('token') : null;
            let response: TJsonApiBody;
            const prodGroup = projectData.prodGroup
                ? projectData.prodGroup
                : context.rootGetters['user/activeProductGroup'];
            if (token) {
                try {
                    response = (await AjaxService.post(
                        ajaxUrls.projects.projectsList,
                        {
                            data: {
                                type: 'projects',
                                attributes: {
                                    prodGroup,
                                    description: projectData.description,
                                    name: projectData.name,
                                },
                            },
                        },
                        token,
                    )) as TJsonApiBody;
                } catch (e) {
                    reject(e);
                    return;
                }
                await context.dispatch(
                    'getMyProjects',
                    context.rootGetters['user/activeProductGroup'],
                );
                resolve();
            } else {
                reject(new Error('Token exipred'));
            }
        });
    },
    async getSingleProject(context, id) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        let singleProject;
        if (token) {
            try {
                singleProject = await AjaxService.get(
                    ajaxUrls.projects.singleProject(id),
                    token,
                );
            } catch (e) {
                return Promise.reject(e);
            }
        } else {
            context.dispatch('user/logoutUser', { root: true });
            throw new Error("Token doesn't exist");
        }
        context.commit('setActiveProject', singleProject);
        return Promise.resolve();
    },

    async deleteProjectItem(context, id) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (token) {
            try {
                await AjaxService.delete(
                    ajaxUrls.projects.projectItemWithId(id),
                    token,
                );
            } catch (e) {
                return Promise.reject(e);
            }
        } else {
            context.dispatch('user/logoutUser', { root: true });
            throw new Error("Token doesn't exist");
        }
        return Promise.resolve();
    },

    async addProjectItem(context, projectItemData) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (token) {
            try {
                await AjaxService.post(
                    ajaxUrls.projects.projectItem,
                    {
                        data: {
                            type: 'project_items',
                            attributes: {
                                prodGroup: projectItemData.prodGroup,
                            },
                            relationships: {
                                brand: {
                                    data: {
                                        type: 'brands',
                                        id: projectItemData.brandId,
                                    },
                                },
                                user: {
                                    data: {
                                        type: 'users',
                                        id: '1',
                                    },
                                },
                                projects: {
                                    data: [
                                        {
                                            type: 'projects',
                                            id: projectItemData.projectId,
                                        },
                                    ],
                                },
                            },
                        },
                    },
                    token,
                );
            } catch (e) {
                return Promise.reject(e);
            }
        } else {
            context.dispatch('user/logoutUser', { root: true });
            throw new Error("Token doesn't exist");
        }
        return Promise.resolve();
    },

    async getProjectItems(context) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        let allProjectItems;
        if (token) {
            try {
                allProjectItems = await AjaxService.get(
                    ajaxUrls.projects.contractItem(
                        context.rootGetters['user/activeProductGroup'],
                    ),
                    token,
                );
            } catch (e) {
                return Promise.reject(e);
            }
        } else {
            context.dispatch('user/logoutUser', { root: true });
            throw new Error("Token doesn't exist");
        }
        context.commit('setAllProjectItems', allProjectItems);
        return Promise.resolve();
    },

    async getSingleReport(context, reportId) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        let singleReport;
        if (token) {
            try {
                singleReport = await AjaxService.get(
                    ajaxUrls.projects.singleProjectDetails(reportId),
                    token,
                );
            } catch (e) {
                return Promise.reject(e);
            }
        } else {
            context.dispatch('user/logoutUser', { root: true });
            throw new Error("Token doesn't exist");
        }
        context.commit('setActiveSingleReport', singleReport);
        return Promise.resolve();
    },
    async downloadBrand(context, payload) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        let brand: string;
        try {
            brand = (await AjaxService.get(
                ajaxUrls.projects.brandDownload(
                    payload.id,
                    payload.projectName,
                ),
                token,
            )) as string;
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(brand);
    },
    async downloadMultipleBrands(context, payload) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        let brand: string;
        try {
            brand = (await AjaxService.get(
                ajaxUrls.projects.brandMultipleDownload(
                    payload.ids,
                    payload.projectName,
                ),
                token,
            )) as string;
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(brand);
    },
    async downloadStatisticalAnalysisPDF(context, payload) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        let statisticalAnalysis: string;
        try {
            statisticalAnalysis = (await AjaxService.get(
                ajaxUrls.projects.statisticalAnalysisDownloadPDF(
                    payload.ids,
                    context.rootGetters['user/activeProductGroup'],
                    payload.projectName,
                    payload.revenueForecastType,
                ),
                token,
            )) as string;
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(statisticalAnalysis);
    },
    async downloadStatisticalAnalysisExcel(context, payload) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        let statisticalAnalysis: string;
        try {
            statisticalAnalysis = (await AjaxService.get(
                ajaxUrls.projects.statisticalAnalysisDownloadExcel(
                    payload.ids,
                    context.rootGetters['user/activeProductGroup'],
                    payload.projectName,
                    payload.revenueForecastType,
                ),
                token,
            )) as string;
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(statisticalAnalysis);
    },

    async getProjectOverview(context) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        const activeProductGroup =
            context.rootGetters['user/activeProductGroup'];
        let projectOverview: string;
        try {
            projectOverview = (await AjaxService.get(
                ajaxUrls.projects.projectOverview(activeProductGroup),
                token,
            )) as string;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setProjectOverview', projectOverview);
        return Promise.resolve();
    },
};

function moveMainFolderToBottom(projectList: TJsonApiBody) {
    const jsonApiData = projectList.data;
    let mainFolderIndex: number;
    if (Array.isArray(jsonApiData)) {
        mainFolderIndex = jsonApiData.findIndex((element) => {
            if (element.attributes) {
                return element.attributes.isMain;
            }
        });

        if (mainFolderIndex !== -1) {
            const mainFolder = jsonApiData[mainFolderIndex];
            jsonApiData.splice(mainFolderIndex, 1);
            jsonApiData.splice(jsonApiData.length, 0, mainFolder);
        }
    }
    projectList.data = jsonApiData;
}

export default actions;
